import type {
  GaragePermitRate,
  GarageGuestPassSetting,
  GarageGuestPassInfo,
} from '@/types/reserve.types';
import { isUpcomingRate, type UpcomingRateLike } from '@/lib/upcoming-permit-products';

export type ParkingPassSource = GarageGuestPassSetting | GarageGuestPassInfo | undefined;

function normalizeParkingPass(
  source: ParkingPassSource
): { available: boolean; purchaseRate: number; count: number } {
  if (!source) return { available: false, purchaseRate: 0, count: 0 };
  const v2 = source as GarageGuestPassInfo;
  const v1 = source as GarageGuestPassSetting;
  const available =
    typeof v2.available === 'boolean' ? v2.available : !!v1.enableParkingPassAccounts;
  const purchaseRate = Number(v2.purchaseRate ?? v1.purchaseRate ?? 0);
  const count = Number(v2.minimumPasses ?? v1.initialParkingPass ?? 0);
  return { available, purchaseRate, count };
}

interface UserLike {
  customAttributes?: { [company: string]: { [key: string]: string } };
}

export interface CustomPaymentOption {
  validationName?: string;
  customAttributeKey?: string;
  description?: string;
  documentId?: string;
}

/**
 * The only fields payment eligibility reads. Structural rather than tied to
 * GaragePermitRate so a coupon's PermitRateOutput qualifies without a cast.
 */
export interface PaymentEligibilityFields {
  paymentOptions?: string[];
  autoPayMode?: string;
  customPaymentOptions?: CustomPaymentOption[];
  /**
   * Server-resolved Discount-Program eligibility for the current user (PB-4739).
   * When true, the rate was granted to this authenticated user via a Discount Program
   * and must stay visible regardless of the custom-payment-attribute gate.
   */
  discountApplicable?: boolean;
}

const STANDARD_PAYMENT_KEYS = ['CREDITCARD', 'ECHECK', 'CHECK', 'PREPAID'];

function rateHasStandardPayment(rate: PaymentEligibilityFields): boolean {
  return (rate.paymentOptions ?? []).some((opt) => STANDARD_PAYMENT_KEYS.includes(opt));
}

// Mirrors legacy getPaymentsEnabledForUser().customPaymentOptions. [] for guests:
// the user's attribute value is the payment token, so nothing is selectable without a profile.
export function getEligibleCustomPaymentOptions(
  rate: PaymentEligibilityFields,
  company: string,
  user: UserLike | null | undefined
): CustomPaymentOption[] {
  if (!(rate.paymentOptions ?? []).includes('CUSTOM_OPTIONS')) return [];
  if (!user) return [];
  const userKeys = Object.keys(user.customAttributes?.[company] ?? {});
  if (userKeys.length === 0) return [];
  return (rate.customPaymentOptions ?? []).filter(
    (p) => !!p.customAttributeKey && userKeys.includes(p.customAttributeKey)
  );
}

function userMatchesCustomPayment(
  rate: PaymentEligibilityFields,
  company: string,
  user: UserLike | null | undefined
): boolean {
  if (!(rate.paymentOptions ?? []).includes('CUSTOM_OPTIONS')) return false;
  // No profile: keep the rate visible (eligibility resolves once signed in).
  if (!user) return true;
  return getEligibleCustomPaymentOptions(rate, company, user).length > 0;
}

/**
 * Every permit rate card a garage can currently sell — public AND coupon-revealed (PB-5582).
 *
 * ## Invariants:
 * - **The single accessor for "what can this garage sell".** A coupon-revealed card arrives in
 *   its own field and is absent from `permitRates` entirely, so any consumer that reads only
 *   that field silently fails to see it — and the failure is never an error, it is a missing
 *   row, a null summary or a blank screen. Three separate review rounds each found a different
 *   surface that read `permitRates` directly; this exists so the answer is in one place rather
 *   than re-derived at each call site.
 * - **Public first.** A card present in both lists resolves to the public entry, so every
 *   surface agrees on which one won.
 * - **NOT for the eligibility filters.** `filterPermitRatesByEligibility` and
 *   `shouldHideIntroRate` ask whether this CUSTOMER may have a rate; a revealed card is
 *   unlocked by the CODE, not by who holds it, so it is appended after those filters rather
 *   than passed through them. Call sites that feed a filter deliberately keep reading
 *   `permitRates` alone and say so.
 * - **Nor for "the garage's own headline rate".** The advertised rate and the cheapest-rate
 *   fallback are statements about public inventory; an unlocked card is neither advertised nor
 *   a safe thing to substitute for a selection that could not be resolved.
 */
export function sellablePermitRates<T>(
  garage:
    | {
        permitRates?: T[] | null;
        couponRevealedPermitRates?: T[] | null;
      }
    | null
    | undefined
): T[] {
  // Null-tolerant because most callers hold a possibly-absent garage; returning [] keeps every
  // call site free of its own guard, which is where a missed one would reintroduce the bug.
  return [...(garage?.permitRates ?? []), ...(garage?.couponRevealedPermitRates ?? [])];
}

export interface EffectiveWindowFields extends UpcomingRateLike {
  endDate?: string;
}

/**
 * Drops rate cards whose effective window does not cover `quoteDate` (both bounds inclusive).
 *
 * The server filters too, but at whatever date the client sent it — and a date that arrives
 * shifted lands on the previous garage-local day, leaving cards end-dated yesterday sellable
 * (PB-3319). Compares `yyyy-MM-dd` strings, never `Date`s: parsing would re-introduce that
 * shift. An absent bound is kept — synthesized plan rates carry no rate card.
 *
 * Unlike the eligibility filters this asks whether the CARD is live at all, not whether this
 * customer may have it — so a coupon-revealed card is subject to it too.
 *
 * ## Invariants:
 * - `keepUpcoming` waives only the START bound, and only for a card the API flagged upcoming
 *   (PB-5712). A retired card is dropped regardless, and an unflagged card starting after the
 *   quote date is still dropped — that is the shifted-date backstop, not a sellable product.
 * - Only a surface that moves the booking start date onto the product's start may opt in;
 *   elsewhere the customer could book a product before it exists.
 *
 * @param quoteDate permit start date as garage-local `yyyy-MM-dd`
 */
export function filterPermitRatesByEffectiveWindow<T extends EffectiveWindowFields>(
  rates: T[],
  quoteDate: string,
  { keepUpcoming = false }: { keepUpcoming?: boolean } = {}
): T[] {
  if (!quoteDate) return rates;
  return rates.filter((rate) => {
    const startWaived = keepUpcoming && isUpcomingRate(rate);
    if (!startWaived && rate.startDate && rate.startDate > quoteDate) return false;
    if (rate.endDate && rate.endDate < quoteDate) return false;
    return true;
  });
}

export function filterPermitRatesByEligibility<T extends PaymentEligibilityFields>(
  rates: T[],
  company: string,
  user: UserLike | null | undefined
): T[] {
  return rates.filter((rate) => {
    if (!rate.paymentOptions) return true;
    // PB-4739: the server (/v2/garages/search) already resolved this authenticated user's
    // Discount-Program eligibility and stamped `discountApplicable` on the rates it granted.
    // Honor that decision. A discount-program-gated rate (e.g. "Payroll Deduction") carries a
    // CUSTOM_OPTIONS payment whose `customAttributeKey` the linked user need NOT have — the
    // custom-payment gate below is a DIFFERENT mechanism and would otherwise drop a rate the
    // user IS entitled to via the discount program. Non-linked users get discountApplicable
    // false/absent from the server, so nothing leaks.
    if (rate.discountApplicable) return true;
    if (rateHasStandardPayment(rate)) return true;
    if (rate.autoPayMode === 'DISABLE_UPFRONT_NO_AUTOPAY') return true;
    if (userMatchesCustomPayment(rate, company, user)) return true;
    return false;
  });
}

// V2 board cards send only allowedVehicleTypes; coupon outputs send vehicleTypes.
export interface VehicleTypeRestrictedRate {
  vehicleTypes?: readonly string[] | null;
  allowedVehicleTypes?: readonly string[] | null;
}

// Mirrors legacy filterPermitRates; pass the size-mapped type, not the stored oversizeType.
export function isRateForVehicleType(
  rate: VehicleTypeRestrictedRate,
  vehicleType: string | null | undefined
): boolean {
  const restrictedTo = rate.vehicleTypes ?? rate.allowedVehicleTypes;
  if (!restrictedTo?.length || !vehicleType) return true;
  const wanted = vehicleType.toUpperCase();
  return restrictedTo.some((vt) => vt.toUpperCase() === wanted);
}

export function filterPermitRatesByVehicleType<T extends VehicleTypeRestrictedRate>(
  rates: T[],
  vehicleType: string | null | undefined
): T[] {
  return rates.filter((rate) => isRateForVehicleType(rate, vehicleType));
}

/**
 * Card / Bank tabs the monthly-signup picker may offer for the selected rate, narrowed by the
 * rate card's payment options — mirrors legacy `getPaymentsEnabledForUser` and its server port
 * `PermitCalculationServiceImpl.resolvePaymentMethods` (PB-3291).
 *
 * Prefers the server's already-narrowed `enabledPaymentMethods`; falls back to the rate's
 * `paymentOptions` when it isn't loaded (the caller's usePaymentWidget still ANDs the
 * garage/theme gateway flags).
 *
 * ## Invariants:
 * - The server's `enabledPaymentMethods` is intersected with `paymentOptions`, never returned raw.
 *   Why: `paymentOptions` may be narrowed client-side beyond what the server calc knows — the Add
 *   Permit flow locks it to `['CUSTOM_OPTIONS']` when the account has a Custom-Attributes payment
 *   method (PB-238 `accountLockedKey`), but the underlying rate still lists CREDITCARD/ECHECK, so
 *   the server's `enabledPaymentMethods` still contains them. Returning that list raw re-exposed
 *   Card/Bank the account/PRE had restricted away (PB-3694).
 * - Fallback eCheck also requires `allowEcheckPaymentsForMonthlySignUp` (the server set already
 *   applies it). Why: operators restrict eCheck on new signups independently of the garage ACH flag.
 * - Absent options ⇒ no per-rate restriction.
 */
export function getSignupPaymentTabs(
  enabledPaymentMethods: string[] | null | undefined,
  paymentOptions: string[] | null | undefined,
  allowEcheckForSignup: boolean | undefined
): { creditCard: boolean; bank: boolean } {
  // Absent/empty paymentOptions ⇒ no per-rate restriction; otherwise a method must be in it.
  const optionAllows = (key: string): boolean =>
    !paymentOptions || paymentOptions.length === 0 || paymentOptions.includes(key);

  if (enabledPaymentMethods && enabledPaymentMethods.length > 0) {
    // Intersect the server's already-(garage/theme/rate)-narrowed set with paymentOptions, which
    // may carry a further client-only narrowing (e.g. the account-lock to CUSTOM_OPTIONS). Since
    // the server only enables CC/ECHECK when the rate lists them, this is a no-op unless the
    // client narrowed paymentOptions — exactly the case that must hide Card/Bank (PB-3694).
    return {
      creditCard: enabledPaymentMethods.includes('CREDITCARD') && optionAllows('CREDITCARD'),
      bank: enabledPaymentMethods.includes('ECHECK') && optionAllows('ECHECK'),
    };
  }
  if (!paymentOptions || paymentOptions.length === 0) {
    return { creditCard: true, bank: true };
  }
  return {
    creditCard: paymentOptions.includes('CREDITCARD'),
    bank: paymentOptions.includes('ECHECK') && allowEcheckForSignup === true,
  };
}

export function isGarageHasParkingPassOption(source: ParkingPassSource): boolean {
  const { available, purchaseRate, count } = normalizeParkingPass(source);
  return available && purchaseRate > 0 && count > 0;
}

export function parkingPassPackageTotal(source: ParkingPassSource): number {
  if (!isGarageHasParkingPassOption(source)) return 0;
  const { purchaseRate, count } = normalizeParkingPass(source);
  return count * purchaseRate;
}

export function parkingPassPurchaseRate(source: ParkingPassSource): number {
  if (!isGarageHasParkingPassOption(source)) return 0;
  return normalizeParkingPass(source).purchaseRate;
}

export function parkingPassMinimumCount(source: ParkingPassSource): number {
  if (!isGarageHasParkingPassOption(source)) return 0;
  return normalizeParkingPass(source).count;
}
